import axios from 'axios';
import {ChangeEvent, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {ControllerRenderProps, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {HTTP_HOST} from 'shared/consts';
import {ConfigContext} from '../../../app/App.tsx';

type formValues = {
  login: string;
  password: string;
  repeatPassword: string;
  code: string;
};

let lastEnteredPin = '';

export const useRecover = () => {
  const {realm, step} = useParams();
  const defaultTimer = useRef(60);
  const lastActualPath = useRef<string>('');

  const navigate = useNavigate();

  const config = useContext(ConfigContext);

  const [sendCodeText, setSendCodeText] = useState<string>('Отправить код повторно');
  const [sendCodeButtonType, setSendCodeButtonType] = useState<
    'ghost' | 'primary' | 'secondary' | 'outline'
  >('ghost');
  const [sendCodeButtonDisabled, setSendCodeButtonDisabled] = useState<boolean>(true);
  const [pinValues, setpinValues] = useState(['', '', '', '']);
  const [timer, setTimer] = useState<number>(0);
  const [showPass, setShowPass] = useState<boolean>(false);
  const [showConfirmPass, setShowConfirmPass] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEmailBtnDisabled, setIsEmailBtnDisabled] = useState<boolean>(true);

  // eslint-disable-next-line
  const emailPattern = useRef<RegExp>(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,8})+$/);

  const externalRedirect = sessionStorage.getItem('externalRedirect');

  const {
    control,
    formState: {errors},
    register,
    setValue,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
  } = useForm<formValues>({
    defaultValues: {
      login: '',
      password: '',
      repeatPassword: '',
      code: '',
    },
  });

  const checkEmailExists = useCallback(async (email: string): Promise<boolean> => {
    if (!emailPattern.current.test(email)) {
      setIsEmailBtnDisabled(true);
      setError('login', {message: 'Некорректный e-mail'});
      return false;
    }

    const res = await axios.get(`/ecpp/users?email=${encodeURIComponent(email)}`);

    if (res.data.total == 0) {
      setIsEmailBtnDisabled(true);
      setError('login', {message: 'Указанная почта не используется'});
      return false;
    }

    setIsEmailBtnDisabled(false);
    return true;
  }, [setError]);

  const onEmailComplete = useCallback(async () => {
    await checkEmailExists(getValues().login.trim());
  }, [checkEmailExists, getValues]);

  const onEmailSubmit = async (data: formValues) => {
    const email = data.login.trim();

    if (!emailPattern.current.test(email)) {
      return setError('login', {message: 'Некорректный e-mail'});
    }
    if (!email) return setError('login', {message: 'Некорректная почта'});
    if (!(await checkEmailExists(email))) return;

    setIsLoading(true);

    const response = await axios
      .post('/ecpp/sms', {email})
      .catch((err) => {
        if (err.response.data.message) {
          const errStr = Array.isArray(err.response.data.message)
            ? err.response.data.message[0]
            : err.response.data.message;

          toast.error(errStr);
        }
      });

    setTimer(defaultTimer.current);
    setIsLoading(false);

    if (response?.status === 200) {
      navigate(`/oauth/realms/${realm}/recovery-password/2`);
    }
  };

  const onBackClick = (path: string) => {
    setTimer(0);
    navigate(path);
  };

  const onPinComplete = async (value: string[]) => {
    setIsLoading(true);
    const resBody = {
      email: getValues().login.trim(),
      code: value.join(''),
    };
    const res = await axios.post('/ecpp/sms/verify', resBody).catch((err) => {
      if (err.response.data.message) {
        const errStr = Array.isArray(err.response.data.message)
          ? err.response.data.message[0]
          : err.response.data.message;

        toast.error(errStr);
      }
    });

    if (res?.status === 200) {
      setTimer(0);
      lastEnteredPin = value.join('');
      setpinValues(['', '', '', '']);

      setIsLoading(false);
      navigate(`/oauth/realms/${realm}/recovery-password/3`);
    }
  };

  const reSendPin = async () => {
    await onEmailSubmit(getValues());
  };

  const changePass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'password'>
  ) => {
    field.onChange(event);
  };

  const changeConfirmPass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'repeatPassword'>
  ) => {
    field.onChange(event);
  };

  const onPassSubmit = async (data: formValues) => {
    if (data.password !== data.repeatPassword) {
      setError('password', {message: 'Пароли не совпадают'});
      setError('repeatPassword', {message: 'Пароли не совпадают'});
      return;
    }

    const body = {
      login: data.login.trim(),
      password: data.password.trim(),
      code: lastEnteredPin,
    };

    setIsLoading(true);

    const resParams = {
      headers: {
        authorization: `Basic ${btoa('web:')}`,
      },
    };
    const response = await axios
      .post(`${HTTP_HOST}/oauth/realms/${realm}/recovery-password`, body, resParams)
      .catch((err) => {
        if (err.response.data.fields?.length) {
          for (let index = 0; index < err.response.data.fields.length; index++) {
            const element = err.response.data.fields[index];
            toast.error(element.errorMessage);
          }
        } else {
          if (err.response.data.message) {
            if (Array.isArray(err.response.data.message)) {
              for (let index = 0; index < err.response.data.message.length; index++) {
                const error = err.response.data.message[index];
                toast.error(error);
              }
            } else {
              toast.error(err.response.data.message);
            }
          }
        }
      });

    if (response?.data?.code) {
      const params = {
        grant_type: 'code',
        scope: 'openid',
        code: response?.data?.code,
        client_id: 'web',
        redirect_uri: `${window.location.origin || config.VITE_AUTH_HOST}/`,
        auth_mode: 'cookie',
      };

      const path = `/ecpp/oauth/realms/${realm}/token?${new URLSearchParams(params).toString()}`;

      const tokenRes = await axios.post(path, undefined, resParams).catch((err) => {
        if (err.response.data.message) {
          if (Array.isArray(err.response.data.message)) {
            for (let index = 0; index < err.response.data.message.length; index++) {
              const error = err.response.data.message[index];
              toast.error(error);
            }
          } else {
            toast.error(err.response.data.message);
          }
        }
      });

      if (tokenRes?.data) {
        const target = externalRedirect
          ? `${externalRedirect}${externalRedirect.includes('?') ? '&' : '?'}code=${response.data.code}`
          : '/';

        window.location.replace(target);
      }
    }

    setIsLoading(false);
  };

  const passValidate = () => {
    const {password, repeatPassword} = getValues();

    if (password !== repeatPassword) {
      return 'Пароли не совпадают';
    }

    clearErrors();
    return true;
  };

  useEffect(() => {
    if (!step || !getValues().login) {
      setTimer(0);
      navigate(`/oauth/realms/${realm}/recovery-password/1`);
    }
    if (step === '1' || step === '2' || step === '3') {
      lastActualPath.current = window.location.pathname;
    } else if (lastActualPath.current?.trim()) {
      navigate(lastActualPath.current);
    } else {
      navigate(`/oauth/realms/${realm || 'main'}/recovery-password/1`);
    }

    const firstInputOnPage = document.querySelector('input');
    firstInputOnPage?.focus();
  }, [step, realm]);

  useEffect(() => {
    if (timer > 0) {
      setSendCodeButtonDisabled(true);
      setSendCodeButtonType('ghost');
      setSendCodeText(`Отправить код повторно (${timer} сек.)`);
      setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
    } else {
      setSendCodeButtonDisabled(false);
      setSendCodeButtonType('outline');
      setSendCodeText(`Отправить код повторно`);
    }
  }, [timer, setTimer, setSendCodeText]);

  useEffect(() => {
    if (pinValues.filter((el) => el.trim()).length === pinValues.length) onPinComplete(pinValues);
  }, [pinValues]);

  return {
    control,
    realm,
    step,
    sendCodeText,
    sendCodeButtonType,
    sendCodeButtonDisabled,
    errors,
    pinValues,
    showPass,
    showConfirmPass,
    isLoading,
    isEmailBtnDisabled,
    emailPattern,
    register,
    onEmailSubmit,
    onBackClick,
    handleSubmit,
    setValue,
    reSendPin,
    setpinValues,
    onPassSubmit,
    setShowPass,
    setShowConfirmPass,
    changePass,
    changeConfirmPass,
    clearErrors,
    passValidate,
    onEmailComplete,
    setIsEmailBtnDisabled,
  };
};
